import { useState } from 'react'
import { S, C, changeColor } from './vcStyles.js'
import { shortId, initials, colorForUser, summarizeChanges } from '../../lib/versionHistory.js'
import { fmtDate, fmtTime } from './vcStyles.js'
import { conflictKey, buildResolutions, resolvedCount, allResolved, pickAll, conflictKind } from '../../lib/mergeResolve.js'

// Side-panel confirmation flows, modeled on PlayCanvas's VC side panels. Each
// slides over the right pane with a title/note, a body, and Cancel/Confirm.

export function SidePanelShell({ title, note, children, onCancel, onConfirm, confirmText, confirmDisabled, confirmDanger, busy }) {
  return (
    <div style={S.sidePanel}>
      <div style={S.sideHeader}>
        <div style={S.sideTitle}>{title}</div>
        {note && <div style={S.sideNote}>{note}</div>}
      </div>
      <div style={S.sideBody}>{children}</div>
      <div style={S.sideButtons}>
        <button style={S.ghost} disabled={busy} onClick={onCancel}>Cancel</button>
        {onConfirm && (
          <button
            style={confirmDanger ? S.danger : S.primary}
            disabled={confirmDisabled || busy}
            onClick={onConfirm}
          >
            {busy ? 'Working…' : confirmText}
          </button>
        )}
      </div>
    </div>
  )
}

function CheckpointBox({ cp }) {
  return (
    <div style={S.box}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ ...S.avatar, background: colorForUser(cp.created_by || cp.id) }}>{initials(cp.created_by)}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{cp.label || <span style={S.dim}>(no description)</span>}</div>
          <div style={S.meta}>{fmtDate(cp.created_at)} · {shortId(cp.id)} · {cp.created_by || 'unknown'} · {fmtTime(cp.created_at)}</div>
        </div>
      </div>
    </div>
  )
}

function ChangeList({ changes, max = 50 }) {
  if (!changes) return <div style={S.dim}>Loading…</div>
  if (changes.length === 0) return <div style={S.dim}>No changes.</div>
  const s = summarizeChanges(changes)
  return (
    <div style={S.diffBox}>
      <div style={{ marginBottom: 6 }}>
        <span style={{ color: C.added }}>+{s.added}</span>{' '}
        <span style={{ color: C.modified }}>~{s.modified}</span>{' '}
        <span style={{ color: C.removed }}>-{s.removed}</span>
      </div>
      {changes.slice(0, max).map((c, i) => (
        <div key={i} style={S.diffRow}>
          <span style={{ color: changeColor(c.type) }}>{c.type}</span> {c.section}{c.item_id ? ` · ${shortId(c.item_id)}` : ''}
        </div>
      ))}
      {changes.length > max && <div style={S.dim}>+{changes.length - max} more…</div>}
    </div>
  )
}

// Restore: reconcile live state to a checkpoint. The backend always writes a
// pre-restore backup checkpoint first, so the restore is itself reversible.
export function RestoreFlow({ cp, busy, onCancel, onConfirm }) {
  return (
    <SidePanelShell
      title={`Restore checkpoint "${shortId(cp.id)}"?`}
      note="The character's live state is reconciled to this checkpoint and the blueprint is appended as a new version. A backup checkpoint of the current state is created automatically first, so this is reversible."
      onCancel={onCancel}
      onConfirm={onConfirm}
      confirmText="Restore checkpoint"
      busy={busy}
    >
      <CheckpointBox cp={cp} />
    </SidePanelShell>
  )
}

// Hard reset: type-to-confirm, mirrors PlayCanvas exactly.
export function HardResetFlow({ cp, branch, busy, onCancel, onConfirm }) {
  const [typed, setTyped] = useState('')
  const armed = typed.trim().toLowerCase() === 'hard reset'
  return (
    <SidePanelShell
      title="Hard reset to checkpoint?"
      note={`Every checkpoint after this one on "${branch?.name}" will be PERMANENTLY DELETED and the live character reset to it. This cannot be undone.`}
      onCancel={onCancel}
      onConfirm={armed ? onConfirm : undefined}
      confirmText="Hard reset to checkpoint"
      confirmDisabled={!armed}
      confirmDanger
      busy={busy}
    >
      <CheckpointBox cp={cp} />
      <div style={{ marginTop: 6 }}>
        <div style={{ ...S.dim, marginBottom: 4 }}>Type <b style={{ color: C.danger }}>hard reset</b> to confirm:</div>
        <input
          style={S.input}
          value={typed}
          autoFocus
          placeholder="hard reset"
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && armed && !busy) onConfirm() }}
        />
      </div>
    </SidePanelShell>
  )
}

// New branch from a branch head or a specific checkpoint.
export function NewBranchFlow({ source, busy, onCancel, onConfirm }) {
  const [name, setName] = useState('')
  const valid = name.trim().length > 0
  return (
    <SidePanelShell
      title="Create a new branch"
      note="A branch is an independent line of development. It starts from the source below and diverges from there."
      onCancel={onCancel}
      onConfirm={valid ? () => onConfirm(name.trim()) : undefined}
      confirmText="Create branch"
      confirmDisabled={!valid}
      busy={busy}
    >
      <div style={S.box}>
        <div style={S.dim}>Branching from</div>
        <div style={{ marginTop: 3 }}>{source.label}</div>
      </div>
      <div style={{ textAlign: 'center', color: C.textDim, margin: '2px 0 8px' }}>↓</div>
      <div>
        <div style={{ ...S.dim, marginBottom: 4 }}>New branch name</div>
        <input
          style={S.input}
          value={name}
          autoFocus
          placeholder="e.g. feature-rework"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && valid && !busy) onConfirm(name.trim()) }}
        />
      </div>
    </SidePanelShell>
  )
}

// New checkpoint: shows pending working changes (vs branch head) + a describe
// box. Folds our "uncheckpointed changes" view into the commit dialog.
export function NewCheckpointFlow({ changes, changesErr, busy, onCancel, onConfirm }) {
  const [label, setLabel] = useState('')
  return (
    <SidePanelShell
      title="Create checkpoint"
      note="Snapshots the character's full content-browser state into a new immutable, attributed checkpoint on this branch."
      onCancel={onCancel}
      onConfirm={() => onConfirm(label.trim())}
      confirmText="Create checkpoint"
      busy={busy}
    >
      <div style={{ ...S.dim, marginBottom: 6 }}>Changes since the last checkpoint</div>
      {changesErr ? <div style={{ color: C.danger }}>⚠ {changesErr}</div> : <ChangeList changes={changes} />}
      <div style={{ marginTop: 12 }}>
        <textarea
          style={{ ...S.input, minHeight: 56, resize: 'vertical', fontFamily: 'inherit' }}
          placeholder="Describe this checkpoint (optional)…"
          value={label}
          autoFocus
          onChange={(e) => setLabel(e.target.value)}
        />
      </div>
    </SidePanelShell>
  )
}

// Read-only diff of one checkpoint vs another (View changes / Compare result).
export function DiffFlow({ title, from, to, changes, error, busy, onCancel }) {
  return (
    <SidePanelShell title={title} onCancel={onCancel} busy={busy}>
      {(from || to) && (
        <div style={{ marginBottom: 8 }}>
          {from && <span style={S.pill}>{shortId(from.id)}</span>}{' → '}
          {to && <span style={S.pill}>{shortId(to.id)}</span>}
        </div>
      )}
      {error ? <div style={{ color: C.danger }}>⚠ {error}</div> : <ChangeList changes={changes} />}
    </SidePanelShell>
  )
}

// Merge preview → resolve → apply. Clean merges apply directly. Conflicts get
// an interactive per-conflict resolver: pick a side (theirs/ours) for each,
// with the values viewable — PlayCanvas's click-a-side model, scoped to our
// coarse section/row conflicts (no field-level diff). Apply is gated until
// every conflict has a side; on apply the chosen resolutions go to applyMerge.
export function MergeFlow({ source, dest, preview, error, busy, onCancel, onApply }) {
  const conflicts = preview?.conflicts || []
  const hasConflicts = conflicts.length > 0
  const [choices, setChoices] = useState({})

  const done = resolvedCount(conflicts, choices)
  const ready = allResolved(conflicts, choices)
  const clean = !error && preview && !hasConflicts
  const canApply = !error && preview && (clean || ready)

  const pick = (c, side) => setChoices((m) => ({ ...m, [conflictKey(c)]: side }))
  const takeAll = (side) => setChoices(pickAll(conflicts, side))

  return (
    <SidePanelShell
      title={`Merge "${source?.name}" into "${dest?.name}"`}
      note="A 3-way merge against the branches' common ancestor. Non-conflicting changes auto-merge; pick a side for each conflict below."
      onCancel={onCancel}
      onConfirm={canApply ? () => onApply(buildResolutions(conflicts, choices)) : undefined}
      confirmText={hasConflicts ? `Apply merge (${done}/${conflicts.length})` : 'Apply merge'}
      confirmDisabled={!canApply}
      busy={busy}
    >
      {error && <div style={{ color: C.danger }}>⚠ {error}</div>}
      {!error && !preview && <div style={S.dim}>Computing merge…</div>}
      {clean && <div style={{ color: C.added }}>Clean merge — no conflicts. Apply to write it as a checkpoint on "{dest?.name}".</div>}
      {!error && hasConflicts && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ color: ready ? C.added : C.modified }}>
              {done}/{conflicts.length} conflict{conflicts.length === 1 ? '' : 's'} resolved
            </span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button style={S.ghost} onClick={() => takeAll('src')}>Take all theirs</button>
              <button style={S.ghost} onClick={() => takeAll('dst')}>Take all ours</button>
            </span>
          </div>
          {conflicts.map((c) => (
            <ConflictRow key={conflictKey(c)} conflict={c} choice={choices[conflictKey(c)]} onPick={pick} />
          ))}
        </>
      )}
    </SidePanelShell>
  )
}

function ConflictRow({ conflict, choice, onPick }) {
  const [open, setOpen] = useState(false)
  const label = conflict.section + (conflict.item_id ? ` · ${shortId(conflict.item_id)}` : '')
  const sideBtn = (side, text) => (
    <button
      style={choice === side
        ? { ...S.primary, padding: '3px 8px' }
        : { ...S.ghost, ...(choice && choice !== side ? { opacity: 0.55 } : {}) }}
      onClick={() => onPick(conflict, side)}
    >{text}</button>
  )
  return (
    <div style={{ ...S.box, padding: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: choice ? C.added : C.modified }}>{choice ? '●' : '○'}</span>
        <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        <button style={{ ...S.iconBtn, fontSize: 11 }} onClick={() => setOpen((v) => !v)}>{open ? 'hide' : 'view'}</button>
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        {sideBtn('src', 'Use theirs')}
        {sideBtn('dst', 'Use ours')}
      </div>
      {open && (
        <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
          <ValueCol label="base" value={conflict.base} kind={conflictKind(conflict)} side="base" />
          <ValueCol label="theirs (src)" value={conflict.src} kind={conflictKind(conflict)} side="src" />
          <ValueCol label="ours (dst)" value={conflict.dst} kind={conflictKind(conflict)} side="dst" />
        </div>
      )}
    </div>
  )
}

function ValueCol({ label, value, kind, side }) {
  const isDeleted = (kind === 'deleted-in-src' && side === 'src') || (kind === 'deleted-in-dst' && side === 'dst')
  const deletedText = side === 'src' ? 'Deleted on source branch' : 'Deleted on destination branch'

  return (
    <div>
      <div style={{ ...S.dim, fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '.08em' }}>{label}</div>
      {isDeleted ? (
        <div style={{ margin: '2px 0 0', ...S.deletedChip }}>{deletedText}</div>
      ) : (
        <pre style={{ margin: '2px 0 0', fontSize: 10, maxHeight: 140, overflow: 'auto', background: C.bgRow, border: `1px solid ${C.border}`, borderRadius: 3, padding: 5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {(() => { let text; try { text = value == null ? '—' : JSON.stringify(value, null, 1) } catch { text = String(value) } return text })()}
        </pre>
      )}
    </div>
  )
}
